import { useCallback, useEffect, useRef, useState } from "react";
import type { OrbState } from "../components/ui/ai-orb/orb.types.js";
import type { SirraLine } from "../lib/onboardingScript.js";
import { useVirtualHr } from "../lib/virtualHr.js";

// Sirra's voice for the onboarding wizard.
//
// Only the recorded audio is ever spoken: scripted lines play as a natural female voice
// (/voice/sirra/<id>.mp3, made by `pnpm --filter @hrms/web voice:generate`). That audio goes through
// a Web Audio analyser, so the orb follows the real loudness of her voice. The browser's own
// text-to-speech voice is never used: a line with no recording (or one that fails to play) is shown
// as a caption only, and the next line follows.
//
// Browsers refuse to play sound before the person has interacted with the page, so the first
// line waits for their first click or keypress. It also waits until the tab is on screen.
//
// `say` speaks a line; `show` only captions it (a returning visit), and Replay speaks it.
//
// The mute choice lives in sessionStorage: it survives a refresh, but every new browser tab
// starts with the voice on.
//
// Getting the voice to the ear without breaking up, even on a slow connection:
//  - the recordings are small MP3s (about 8 KB per second of speech), which the browser streams
//    while it plays;
//  - the lines she is about to need are fetched ahead of time (`prefetch`), so most are in the cache;
//  - a line starts only once enough of it has arrived to play straight through (or after a short
//    wait), and only after the audio engine is running, so the first words are not clipped or choppy;
//  - while that happens the orb shows "thinking" and the card says the voice is loading.

const MUTE_KEY = "sirra_guide_muted";
const VOICE_BASE = "/voice/sirra/";
/** How long a line may wait for enough audio to arrive before it plays with what it has. */
const BUFFER_WAIT_MS = 3500;
/** How long to wait for the audio engine to start before playing anyway. */
const ENGINE_WAIT_MS = 400;

export type SayInput = SirraLine | SirraLine[] | string;
type Item = { text: string; id?: string };
type TypeMode = "hold" | "audio" | "silent";

function readMuted(): boolean {
  try { return sessionStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}

const voiceUrl = (id: string): string => `${VOICE_BASE}${id}.mp3`;

const warmed = new Set<string>();
/**
 * Starts downloading recordings into the browser's cache, so they play at once when asked for.
 * Skipped when the person has asked their browser to save data.
 */
export function prefetchVoice(lines: ReadonlyArray<{ id?: string | undefined }>): void {
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  if (saveData) return;
  for (const { id } of lines) {
    if (!id || warmed.has(id)) continue;
    warmed.add(id);
    fetch(voiceUrl(id), { priority: "low" } as RequestInit)
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); })
      .catch(() => warmed.delete(id)); // try again next time it is asked for
  }
}

/** Resolves when the element has enough audio to play to the end, or after `maxWaitMs` (or on error). */
const whenBuffered = (a: HTMLAudioElement, maxWaitMs: number): Promise<void> =>
  new Promise((resolve) => {
    if (a.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) { resolve(); return; }
    const done = () => {
      window.clearTimeout(timer);
      a.removeEventListener("canplaythrough", done);
      a.removeEventListener("error", done);
      resolve();
    };
    const timer = window.setTimeout(done, maxWaitMs);
    a.addEventListener("canplaythrough", done);
    a.addEventListener("error", done);
  });

/** Starts a suspended audio engine and waits for it (briefly), so playback does not begin into silence. */
async function engineRunning(c: AudioContext | null): Promise<void> {
  if (!c || c.state === "running") return;
  await Promise.race([c.resume().catch(() => undefined), new Promise<void>((r) => window.setTimeout(r, ENGINE_WAIT_MS))]);
}

const tabVisible = (): boolean => typeof document === "undefined" || document.visibilityState === "visible";

const toItems = (input: SayInput): Item[] =>
  typeof input === "string" ? [{ text: input }] : (Array.isArray(input) ? input : [input]).map((l) => ({ text: l.text, id: l.id }));

export function useVirtualHrGuide() {
  const { shortName } = useVirtualHr();
  const nameRef = useRef(shortName);
  nameRef.current = shortName;

  const [caption, setCaption] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [muted, setMutedState] = useState(readMuted);
  const [waitingForTap, setWaitingForTap] = useState(false);
  const [busy, setBusy] = useState(false);
  // The voice is on its way (a line is arriving, or playback stalled for more data).
  const [buffering, setBuffering] = useState(false);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  // The caption is typed out as she speaks. `typing` is stepped by one animation-frame loop below.
  const [typed, setTyped] = useState("");
  // "hold" = waiting for the voice to actually start (a tap, or the audio loading): nothing is typed yet.
  const typing = useRef<{ full: string; shown: number; mode: TypeMode }>({ full: "", shown: 0, mode: "silent" });

  const mutedRef = useRef(muted);
  const touched = useRef(false);
  const pending = useRef<SayInput | null>(null);
  const lastInput = useRef<SayInput | null>(null);
  const queue = useRef<Item[]>([]);
  const run = useRef(0); // bumps on every new request, so late callbacks from a cancelled one are ignored
  const audio = useRef<HTMLAudioElement | null>(null);
  const ctx = useRef<AudioContext | null>(null);

  const beginTyping = useCallback((text: string, mode: TypeMode = "silent") => {
    const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    typing.current = { full: text, shown: reduced ? text.length : 0, mode };
    setTyped(reduced ? text : "");
  }, []);

  const stopAll = useCallback(() => {
    run.current++;
    queue.current = [];
    const a = audio.current;
    if (a) { a.onended = null; a.onerror = null; a.onplaying = null; a.onwaiting = null; a.pause(); a.currentTime = 0; }
    setSpeaking(false);
    setBuffering(false);
  }, []);

  /**
   * The audio element and its Web Audio analyser, made once. Called on the person's first click as well
   * as on the first line, so the engine is already running (and the output device awake) by then.
   */
  const ensureGraph = useCallback((): HTMLAudioElement | null => {
    if (audio.current) return audio.current;
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return null;
    const a = new Audio();
    a.preload = "auto";
    const c = new Ctx();
    const source = c.createMediaElementSource(a);
    const an = c.createAnalyser();
    an.fftSize = 1024;
    source.connect(an);
    an.connect(c.destination);
    audio.current = a;
    ctx.current = c;
    setAnalyser(an);
    void c.resume().catch(() => undefined);
    return a;
  }, []);

  const playNext = useCallback((token: number) => {
    if (token !== run.current) return;
    const item = queue.current.shift();
    if (!item) { setSpeaking(false); setBuffering(false); return; }

    // A line that says the assistant's name was recorded as "Sirra"; if it was renamed, there is no
    // matching recording, so that line is caption-only.
    const renamed = nameRef.current !== "Sirra";
    const text = item.id === "intro" && renamed ? item.text.replace(/Sirra/g, nameRef.current) : item.text;
    setCaption(text);
    beginTyping(text, "hold"); // typing starts when her voice does

    // No recording to play (or it failed): type the caption on its own, then move on to the next line.
    let failed = false;
    const captionOnly = () => {
      if (failed || token !== run.current) return;
      failed = true;
      setSpeaking(false);
      setBuffering(false);
      typing.current.mode = "silent";
      window.setTimeout(() => playNext(token), 800 + (text.length / 45) * 1000);
    };

    if (!item.id || (item.id === "intro" && renamed)) { captionOnly(); return; }

    // Recorded audio, tapped by an analyser so the orb can follow it.
    const a = ensureGraph();
    if (!a) { captionOnly(); return; }
    a.onended = () => playNext(token);
    a.onerror = captionOnly; // file missing or undecodable
    a.onplaying = () => { if (token === run.current) { setBuffering(false); setSpeaking(true); typing.current.mode = "audio"; } };
    // Playback ran out of audio before the next part arrived: say so, and carry on when it does.
    a.onwaiting = () => { if (token === run.current) setBuffering(true); };
    a.src = voiceUrl(item.id);

    // Start only once enough has arrived to play straight through and the audio engine is running, so the
    // first words are neither choppy nor clipped. If that takes too long, play with what there is.
    setBuffering(true);
    void (async () => {
      await whenBuffered(a, BUFFER_WAIT_MS);
      if (token !== run.current) return; // replaced or stopped while waiting
      await engineRunning(ctx.current);
      if (token !== run.current) return;
      a.play().catch(captionOnly);
    })();
  }, [beginTyping, ensureGraph]);

  const start = useCallback((input: SayInput) => {
    stopAll();
    const token = run.current;
    queue.current = toItems(input);
    playNext(token);
  }, [stopAll, playNext]);

  const activated = useCallback(() =>
    touched.current ||
    (typeof navigator !== "undefined" && "userActivation" in navigator &&
      (navigator as Navigator & { userActivation: { hasBeenActive: boolean } }).userActivation.hasBeenActive), []);

  /**
   * Shows the line(s) as captions and plays their recordings one after another (unless muted).
   * In a tab that is not on screen, or before the first click, it waits and starts when it can.
   */
  const say = useCallback((input: SayInput) => {
    lastInput.current = input;
    const items = toItems(input);
    setCaption(items[0]?.text ?? "");
    // With sound on, the text waits for her voice; muted, there is no voice to wait for.
    beginTyping(items[0]?.text ?? "", mutedRef.current ? "silent" : "hold");
    if (mutedRef.current) return;
    if (activated() && tabVisible()) { pending.current = null; setWaitingForTap(false); start(input); }
    else { pending.current = input; setWaitingForTap(!activated()); }
  }, [start, beginTyping, activated]);

  /**
   * Shows the last line as a caption, whole and silent (where a spoken run would end up).
   * Replay then speaks all the lines, from the first.
   */
  const show = useCallback((input: SayInput) => {
    lastInput.current = input;
    const items = toItems(input);
    const text = items[items.length - 1]?.text ?? "";
    setCaption(text);
    typing.current = { full: text, shown: text.length, mode: "silent" };
    setTyped(text);
  }, []);

  // The first click or keypress anywhere unlocks sound and plays whatever was waiting.
  useEffect(() => {
    const unlock = () => {
      touched.current = true;
      // Build the audio engine on this very click, while the browser allows it to run, so it is warm when she speaks.
      if (!mutedRef.current) ensureGraph();
      if (pending.current && !mutedRef.current) { const p = pending.current; pending.current = null; setWaitingForTap(false); start(p); }
    };
    // A tab opened in the background speaks when it is brought on screen (if sound is already allowed).
    const shown = () => {
      if (!tabVisible() || !pending.current || mutedRef.current || !activated()) return;
      const p = pending.current; pending.current = null; setWaitingForTap(false); start(p);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    document.addEventListener("visibilitychange", shown);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      document.removeEventListener("visibilitychange", shown);
      stopAll();
      void ctx.current?.close();
      ctx.current = null;
      audio.current = null;
    };
  }, [start, stopAll, activated, ensureGraph]);

  // One loop types the caption. With recorded audio the text keeps pace with the playback position;
  // muted, or with no recording, it types at a steady speed.
  useEffect(() => {
    let raf = 0;
    let prev = performance.now();
    let lastShown = -1;
    const tick = (now: number) => {
      const dt = Math.min((now - prev) / 1000, 0.1);
      prev = now;
      const t = typing.current;
      if (t.shown < t.full.length) {
        let target: number;
        if (t.mode === "hold") {
          target = t.shown;
        } else if (t.mode === "audio") {
          const a = audio.current;
          const dur = a && Number.isFinite(a.duration) && a.duration > 0 ? a.duration : 0;
          target = dur ? Math.ceil(t.full.length * Math.min(1, (a!.currentTime + 0.1) / dur)) : t.shown + dt * 16;
        } else {
          target = t.shown + dt * 45;
        }
        t.shown = Math.max(t.shown, Math.min(t.full.length, target));
      }
      const n = Math.floor(t.shown);
      if (n !== lastShown) { lastShown = n; setTyped(t.full.slice(0, n)); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMutedState(next);
    try { sessionStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    if (next) { stopAll(); typing.current.mode = "silent"; } // finish the line she was saying
    else if (lastInput.current) start(lastInput.current);
  }, [start, stopAll]);

  const replay = useCallback(() => { if (lastInput.current) { touched.current = true; start(lastInput.current); } }, [start]);

  /** Fetches these lines' recordings ahead of time (not when the voice is muted). */
  const prefetch = useCallback((lines: ReadonlyArray<{ id?: string | undefined }>) => {
    if (!mutedRef.current) prefetchVoice(lines);
  }, []);

  const orbState: OrbState = speaking ? "speaking" : busy || buffering ? "thinking" : "idle";

  return {
    say, show, caption, typed, speaking, muted, toggleMute, replay, stop: stopAll, waitingForTap,
    supported: typeof Audio !== "undefined",
    orbState, setBusy, prefetch,
    /** The voice is on its way: a line is arriving, or playback is waiting for more data. */
    buffering,
    /** Analyser on the recorded voice, so the orb follows its loudness. */
    orbAnalyser: analyser,
  };
}

export type VirtualHrGuide = ReturnType<typeof useVirtualHrGuide>;
